import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Pencil, Trash2, Video } from 'lucide-react';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import { EmptyState, StatusPill } from '@/components/app';
import { VideoLessonsTableProps } from '../types';
import { VIDEO_LESSON_TYPE_LABELS, labelOf } from '@/lib/labels';
import { cn } from '@/lib/utils';

export function VideoLessonsTable({
  videoLessons,
  isLoading,
  selectedId,
  onEdit,
  onDelete,
  onToggleActive,
  formatDate,
  onCreate,
}: VideoLessonsTableProps) {
  if (!isLoading && videoLessons.length === 0) {
    return (
      <EmptyState
        icon={<Video />}
        title="Видеоуроков пока нет"
        description="Добавьте первый урок — он появится в библиотеке приложения"
        action={onCreate && <Button variant="secondary" onClick={onCreate}>Добавить первый урок</Button>}
        className="border-t border-border-soft"
      />
    );
  }

  return (
    <Table className="min-w-[860px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-24 text-right">Порядок</TableHead>
          <TableHead>Название</TableHead>
          <TableHead>Тип</TableHead>
          <TableHead>Ссылка</TableHead>
          <TableHead>Обновлён</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="text-right">Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: 4 }, (_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 7 }, (__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full max-w-28" /></TableCell>
                ))}
              </TableRow>
            ))
          : videoLessons.map((lesson) => (
              <TableRow key={lesson.id} selected={selectedId === lesson.id}>
                <TableCell className="text-right tabular-nums text-text-2">{lesson.sort_id}</TableCell>
                {/* Выключенный урок приглушаем: видно, что в приложении его нет. */}
                <TableCell className={cn('font-semibold', !lesson.is_active && 'text-text-2')}>{lesson.title}</TableCell>
                <TableCell>
                  <StatusPill size="sm">{labelOf(VIDEO_LESSON_TYPE_LABELS, lesson.type)}</StatusPill>
                </TableCell>
                <TableCell className="max-w-[260px]">
                  <a
                    href={lesson.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block truncate text-accent-text underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                    title={lesson.url}
                  >
                    {lesson.url}
                  </a>
                </TableCell>
                <TableCell className="whitespace-nowrap tabular-nums text-text-2">{formatDate(lesson.updated_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Switch
                      checked={lesson.is_active}
                      onCheckedChange={() => onToggleActive(lesson)}
                      aria-label={`Показывать в приложении: ${lesson.title}`}
                    />
                    <StatusPill tone={lesson.is_active ? 'pos' : 'neutral'}>
                      {lesson.is_active ? 'В приложении' : 'Скрыт'}
                    </StatusPill>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon-sm" aria-label="Редактировать видеоурок" title="Редактировать" onClick={() => onEdit(lesson)}>
                      <Pencil />
                    </Button>
                    <Button variant="ghost" size="icon-sm" aria-label="Удалить видеоурок" title="Удалить" onClick={() => onDelete(lesson.id)} className="text-neg hover:bg-neg-soft">
                      <Trash2 />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
      </TableBody>
    </Table>
  );
}
